import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Tab,
  Tabs,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Info } from 'lucide-react';
import {
  useFetchGeneticProfileQuery,
  useFetchKnownMutationsQuery,
  usePatchGeneticProfileMutation,
  useCreateGeneticVariantMutation,
  usePatchGeneticVariantMutation,
  useDeleteGeneticVariantMutation,
} from '#store';

const CATEGORY_ORDER = ['METABOLISM', 'CARDIOLOGY', 'DISEASE_RISK'];

function categoryLabelKey(cat) {
  if (!cat) return 'genetics.category-OTHER';
  const k = `genetics.category-${cat}`;
  return CATEGORY_ORDER.includes(cat) ? k : 'genetics.category-OTHER';
}

function parseDrfError(error) {
  const data = error?.data;
  if (!data || typeof data !== 'object') {
    return { fields: {}, global: [] };
  }
  const fields = {};
  const global = [];
  for (const [key, val] of Object.entries(data)) {
    if (key === 'non_field_errors' && Array.isArray(val)) {
      global.push(...val.map(String));
    } else if (key === 'detail') {
      global.push(String(val));
    } else if (Array.isArray(val)) {
      fields[key] = val.map(String).join(' ');
    } else if (val != null) {
      fields[key] = String(val);
    }
  }
  return { fields, global };
}

function variantTitle(v) {
  const km = v.known_mutation;
  if (km && (km.gene_name || km.variant)) {
    return [km.gene_name, km.variant].filter(Boolean).join(' ').trim();
  }
  if (v.custom_gene_name) return v.custom_gene_name;
  return '';
}

function statusBadgeVariant(v) {
  if (
    v.genotype_status === 'NORMAL' ||
    v.apoe_genotype === 'e3/e3' ||
    v.apoe_genotype === 'e2/e3'
  ) {
    return 'success';
  }
  if (v.genotype_status === 'HET' || v.apoe_genotype === 'e3/e4' || v.apoe_genotype === 'e2/e4') {
    return 'warning';
  }
  if (v.genotype_status === 'HOM' || v.apoe_genotype === 'e4/e4') {
    return 'danger';
  }
  return 'secondary';
}

function AddVariantModal({
  show,
  onHide,
  knownMutations,
  onSubmitCatalog,
  onSubmitCustom,
  catalogSubmitting,
  customSubmitting,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('catalog');
  const [mutationId, setMutationId] = useState('');
  const [genotypeStatus, setGenotypeStatus] = useState('UNKNOWN');
  const [apoeGenotype, setApoeGenotype] = useState('unknown');
  const [customGene, setCustomGene] = useState('');
  const [customStatus, setCustomStatus] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [customVariant, setCustomVariant] = useState('');
  const [customNotes, setCustomNotes] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [globalErrors, setGlobalErrors] = useState([]);

  const selectedMutation = useMemo(() => {
    const id = Number(mutationId);
    if (!id) return null;
    return knownMutations.find((m) => m.id === id) ?? null;
  }, [knownMutations, mutationId]);

  const groupedOptions = useMemo(() => {
    const byCat = { METABOLISM: [], CARDIOLOGY: [], DISEASE_RISK: [], OTHER: [] };
    const list = Array.isArray(knownMutations) ? [...knownMutations] : [];
    list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.id ?? 0) - (b.id ?? 0));
    for (const m of list) {
      const c = CATEGORY_ORDER.includes(m.category) ? m.category : 'OTHER';
      byCat[c].push(m);
    }
    return byCat;
  }, [knownMutations]);

  const resetForm = useCallback(() => {
    setTab('catalog');
    setMutationId('');
    setGenotypeStatus('UNKNOWN');
    setApoeGenotype('unknown');
    setCustomGene('');
    setCustomStatus('');
    setCustomCategory('');
    setCustomVariant('');
    setCustomNotes('');
    setFieldErrors({});
    setGlobalErrors([]);
  }, []);

  useEffect(() => {
    if (!show) resetForm();
  }, [show, resetForm]);

  const handleCatalogSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    setGlobalErrors([]);
    const id = Number(mutationId);
    if (!id || !selectedMutation) {
      setFieldErrors({ known_mutation: t('genetics.validation-required') });
      return;
    }
    const body = { known_mutation: id };
    if (selectedMutation.use_standard_genotype) {
      body.genotype_status = genotypeStatus;
    } else {
      body.apoe_genotype = apoeGenotype;
    }
    try {
      await onSubmitCatalog(body);
      onHide();
      resetForm();
    } catch (err) {
      const { fields, global } = parseDrfError(err);
      setFieldErrors(fields);
      setGlobalErrors(global);
    }
  };

  const handleCustomSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    setGlobalErrors([]);
    if (!customGene.trim() || !customStatus.trim()) {
      setFieldErrors({
        ...(!customGene.trim() ? { custom_gene_name: t('genetics.validation-required') } : {}),
        ...(!customStatus.trim() ? { custom_status_text: t('genetics.validation-required') } : {}),
      });
      return;
    }
    const body = {
      custom_gene_name: customGene.trim(),
      custom_status_text: customStatus.trim(),
      custom_category: customCategory.trim() || undefined,
      custom_variant: customVariant.trim() || undefined,
      notes: customNotes.trim() || undefined,
    };
    try {
      await onSubmitCustom(body);
      onHide();
      resetForm();
    } catch (err) {
      const { fields, global } = parseDrfError(err);
      setFieldErrors(fields);
      setGlobalErrors(global);
    }
  };

  const isStandard = selectedMutation?.use_standard_genotype === true;

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('genetics.modal-title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {globalErrors.length > 0 ? (
          <Alert variant='danger' className='mb-3'>
            {globalErrors.map((msg) => (
              <div key={msg}>{msg}</div>
            ))}
          </Alert>
        ) : null}
        <Tabs activeKey={tab} onSelect={(k) => setTab(k || 'catalog')} className='mb-3'>
          <Tab eventKey='catalog' title={t('genetics.tab-catalog')}>
            <Form onSubmit={handleCatalogSubmit} className='mt-3'>
              <p className='text-muted small mb-3'>{t('genetics.modal-catalog-hint')}</p>
              <Form.Group className='mb-3'>
                <Form.Label>{t('genetics.select-mutation')}</Form.Label>
                <Form.Select
                  value={mutationId}
                  onChange={(e) => setMutationId(e.target.value)}
                  isInvalid={!!fieldErrors.known_mutation}
                >
                  <option value=''>{t('genetics.select-mutation-placeholder')}</option>
                  {CATEGORY_ORDER.map((cat) => {
                    const items = groupedOptions[cat] || [];
                    if (!items.length) return null;
                    return (
                      <optgroup key={cat} label={t(categoryLabelKey(cat))}>
                        {items.map((m) => (
                          <option key={m.id} value={String(m.id)}>
                            {[m.gene_name, m.variant].filter(Boolean).join(' ').trim() ||
                              m.gene_name}
                            {m.rs_id ? ` (${m.rs_id})` : ''}
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                  {(groupedOptions.OTHER || []).length > 0 ? (
                    <optgroup label={t('genetics.category-OTHER')}>
                      {(groupedOptions.OTHER || []).map((m) => (
                        <option key={m.id} value={String(m.id)}>
                          {[m.gene_name, m.variant].filter(Boolean).join(' ').trim() || m.gene_name}
                        </option>
                      ))}
                    </optgroup>
                  ) : null}
                </Form.Select>
                <Form.Control.Feedback type='invalid'>
                  {fieldErrors.known_mutation}
                </Form.Control.Feedback>
              </Form.Group>

              {selectedMutation ? (
                isStandard ? (
                  <Form.Group className='mb-3'>
                    <Form.Label>{t('genetics.genotype-label')}</Form.Label>
                    <Form.Select
                      value={genotypeStatus}
                      onChange={(e) => setGenotypeStatus(e.target.value)}
                      isInvalid={!!fieldErrors.genotype_status}
                    >
                      {['NORMAL', 'HET', 'HOM', 'UNKNOWN'].map((v) => (
                        <option key={v} value={v}>
                          {t(`genetics.status-${v}`)}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type='invalid'>
                      {fieldErrors.genotype_status}
                    </Form.Control.Feedback>
                  </Form.Group>
                ) : (
                  <Form.Group className='mb-3'>
                    <Form.Label>{t('genetics.apoe-label')}</Form.Label>
                    <Form.Select
                      value={apoeGenotype}
                      onChange={(e) => setApoeGenotype(e.target.value)}
                      isInvalid={!!fieldErrors.apoe_genotype}
                    >
                      {['e3/e3', 'e3/e4', 'e4/e4', 'e2/e3', 'e2/e2', 'e2/e4', 'unknown'].map(
                        (v) => {
                          const key = v === 'unknown' ? 'apoe-unknown' : `apoe-${v}`;
                          return (
                            <option key={v} value={v}>
                              {t(`genetics.${key}`)}
                            </option>
                          );
                        },
                      )}
                    </Form.Select>
                    <Form.Control.Feedback type='invalid'>
                      {fieldErrors.apoe_genotype}
                    </Form.Control.Feedback>
                  </Form.Group>
                )
              ) : null}

              {Object.entries(fieldErrors).map(([k, msg]) =>
                !['known_mutation', 'genotype_status', 'apoe_genotype'].includes(k) ? (
                  <Alert key={k} variant='warning' className='py-2'>
                    {msg}
                  </Alert>
                ) : null,
              )}

              <div className='d-flex justify-content-end gap-2 mt-2'>
                <Button variant='outline-secondary' type='button' onClick={onHide}>
                  {t('genetics.cancel')}
                </Button>
                <Button type='submit' disabled={catalogSubmitting}>
                  {catalogSubmitting ? (
                    <Spinner animation='border' size='sm' />
                  ) : (
                    t('genetics.submit-add')
                  )}
                </Button>
              </div>
            </Form>
          </Tab>
          <Tab eventKey='custom' title={t('genetics.tab-custom')}>
            <Form onSubmit={handleCustomSubmit} className='mt-3'>
              <Alert variant='info' className='d-flex gap-2 small mb-3'>
                <Info className='flex-shrink-0 mt-1' size={18} strokeWidth={2} aria-hidden />
                <div>
                  <strong className='d-block mb-1'>
                    {t('genetics.modal-custom-banner-title')}
                  </strong>
                  <p className='mb-2'>{t('genetics.modal-custom-banner-p1')}</p>
                  <p className='mb-0'>{t('genetics.modal-custom-banner-p2')}</p>
                </div>
              </Alert>
              <Form.Group className='mb-3'>
                <Form.Label>{t('genetics.custom-gene-name')}</Form.Label>
                <Form.Control
                  value={customGene}
                  onChange={(e) => setCustomGene(e.target.value)}
                  isInvalid={!!fieldErrors.custom_gene_name}
                />
                <Form.Control.Feedback type='invalid'>
                  {fieldErrors.custom_gene_name}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>{t('genetics.custom-status-text')}</Form.Label>
                <Form.Control
                  value={customStatus}
                  onChange={(e) => setCustomStatus(e.target.value)}
                  isInvalid={!!fieldErrors.custom_status_text}
                />
                <Form.Control.Feedback type='invalid'>
                  {fieldErrors.custom_status_text}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>{t('genetics.custom-category')}</Form.Label>
                <Form.Control
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                />
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>{t('genetics.custom-variant')}</Form.Label>
                <Form.Control
                  value={customVariant}
                  onChange={(e) => setCustomVariant(e.target.value)}
                />
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>{t('genetics.variant-notes')}</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={2}
                  value={customNotes}
                  onChange={(e) => setCustomNotes(e.target.value)}
                />
              </Form.Group>
              <div className='d-flex justify-content-end gap-2'>
                <Button variant='outline-secondary' type='button' onClick={onHide}>
                  {t('genetics.cancel')}
                </Button>
                <Button type='submit' disabled={customSubmitting}>
                  {customSubmitting ? (
                    <Spinner animation='border' size='sm' />
                  ) : (
                    t('genetics.submit-add')
                  )}
                </Button>
              </div>
            </Form>
          </Tab>
        </Tabs>
      </Modal.Body>
    </Modal>
  );
}

function EditVariantModal({ show, variant, onHide, onSubmit, submitting }) {
  const { t } = useTranslation();
  const km = variant?.known_mutation;
  const isCatalog = Boolean(km);
  const isStandard = km?.use_standard_genotype === true;

  const [genotypeStatus, setGenotypeStatus] = useState('UNKNOWN');
  const [apoeGenotype, setApoeGenotype] = useState('unknown');
  const [customGene, setCustomGene] = useState('');
  const [customStatus, setCustomStatus] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [customVariant, setCustomVariant] = useState('');
  const [variantNotes, setVariantNotes] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [globalErrors, setGlobalErrors] = useState([]);

  useEffect(() => {
    if (!show || !variant) return;
    setGenotypeStatus(variant.genotype_status || 'UNKNOWN');
    const ag = variant.apoe_genotype;
    setApoeGenotype(ag && String(ag).trim() ? ag : 'unknown');
    setCustomGene(variant.custom_gene_name || '');
    setCustomStatus(variant.custom_status_text || '');
    setCustomCategory(variant.custom_category || '');
    setCustomVariant(variant.custom_variant || '');
    setVariantNotes(variant.notes ?? '');
    setFieldErrors({});
    setGlobalErrors([]);
  }, [show, variant]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    setGlobalErrors([]);
    if (!variant) return;

    let body;
    if (isCatalog) {
      body = { notes: variantNotes.trim() };
      if (isStandard) {
        body.genotype_status = genotypeStatus;
      } else {
        body.apoe_genotype = apoeGenotype;
      }
    } else {
      if (!customGene.trim() || !customStatus.trim()) {
        setFieldErrors({
          ...(!customGene.trim() ? { custom_gene_name: t('genetics.validation-required') } : {}),
          ...(!customStatus.trim()
            ? { custom_status_text: t('genetics.validation-required') }
            : {}),
        });
        return;
      }
      body = {
        custom_gene_name: customGene.trim(),
        custom_status_text: customStatus.trim(),
        custom_category: customCategory.trim(),
        custom_variant: customVariant.trim(),
        notes: variantNotes.trim(),
      };
    }

    try {
      await onSubmit(body);
      onHide();
    } catch (err) {
      const { fields, global } = parseDrfError(err);
      setFieldErrors(fields);
      setGlobalErrors(global);
    }
  };

  if (!variant) return null;

  const title = variantTitle(variant) || t('genetics.custom-fallback');

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('genetics.edit-variant-title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='text-muted small mb-3'>{title}</p>
        {globalErrors.length > 0 ? (
          <Alert variant='danger' className='mb-3'>
            {globalErrors.map((msg) => (
              <div key={msg}>{msg}</div>
            ))}
          </Alert>
        ) : null}
        <Form onSubmit={handleSubmit}>
          {isCatalog ? (
            <>
              {isStandard ? (
                <Form.Group className='mb-3'>
                  <Form.Label>{t('genetics.genotype-label')}</Form.Label>
                  <Form.Select
                    value={genotypeStatus}
                    onChange={(e) => setGenotypeStatus(e.target.value)}
                    isInvalid={!!fieldErrors.genotype_status}
                  >
                    {['NORMAL', 'HET', 'HOM', 'UNKNOWN'].map((v) => (
                      <option key={v} value={v}>
                        {t(`genetics.status-${v}`)}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    {fieldErrors.genotype_status}
                  </Form.Control.Feedback>
                </Form.Group>
              ) : (
                <Form.Group className='mb-3'>
                  <Form.Label>{t('genetics.apoe-label')}</Form.Label>
                  <Form.Select
                    value={apoeGenotype}
                    onChange={(e) => setApoeGenotype(e.target.value)}
                    isInvalid={!!fieldErrors.apoe_genotype}
                  >
                    {['e3/e3', 'e3/e4', 'e4/e4', 'e2/e3', 'e2/e2', 'e2/e4', 'unknown'].map((v) => {
                      const key = v === 'unknown' ? 'apoe-unknown' : `apoe-${v}`;
                      return (
                        <option key={v} value={v}>
                          {t(`genetics.${key}`)}
                        </option>
                      );
                    })}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    {fieldErrors.apoe_genotype}
                  </Form.Control.Feedback>
                </Form.Group>
              )}
              <Form.Group className='mb-3'>
                <Form.Label>{t('genetics.variant-notes')}</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={2}
                  value={variantNotes}
                  onChange={(e) => setVariantNotes(e.target.value)}
                  isInvalid={!!fieldErrors.notes}
                />
                <Form.Control.Feedback type='invalid'>{fieldErrors.notes}</Form.Control.Feedback>
              </Form.Group>
            </>
          ) : (
            <>
              <Form.Group className='mb-3'>
                <Form.Label>{t('genetics.custom-gene-name')}</Form.Label>
                <Form.Control
                  value={customGene}
                  onChange={(e) => setCustomGene(e.target.value)}
                  isInvalid={!!fieldErrors.custom_gene_name}
                />
                <Form.Control.Feedback type='invalid'>
                  {fieldErrors.custom_gene_name}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>{t('genetics.custom-status-text')}</Form.Label>
                <Form.Control
                  value={customStatus}
                  onChange={(e) => setCustomStatus(e.target.value)}
                  isInvalid={!!fieldErrors.custom_status_text}
                />
                <Form.Control.Feedback type='invalid'>
                  {fieldErrors.custom_status_text}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>{t('genetics.custom-category')}</Form.Label>
                <Form.Control
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  isInvalid={!!fieldErrors.custom_category}
                />
                <Form.Control.Feedback type='invalid'>
                  {fieldErrors.custom_category}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>{t('genetics.custom-variant')}</Form.Label>
                <Form.Control
                  value={customVariant}
                  onChange={(e) => setCustomVariant(e.target.value)}
                  isInvalid={!!fieldErrors.custom_variant}
                />
                <Form.Control.Feedback type='invalid'>
                  {fieldErrors.custom_variant}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>{t('genetics.variant-notes')}</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={2}
                  value={variantNotes}
                  onChange={(e) => setVariantNotes(e.target.value)}
                  isInvalid={!!fieldErrors.notes}
                />
                <Form.Control.Feedback type='invalid'>{fieldErrors.notes}</Form.Control.Feedback>
              </Form.Group>
            </>
          )}
          <div className='d-flex justify-content-end gap-2'>
            <Button variant='outline-secondary' type='button' onClick={onHide}>
              {t('genetics.cancel')}
            </Button>
            <Button type='submit' disabled={submitting}>
              {submitting ? <Spinner animation='border' size='sm' /> : t('genetics.save-changes')}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default function GeneticProfilePage() {
  const { t } = useTranslation();
  const [source, setSource] = useState('');
  const [notes, setNotes] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const {
    data: profile,
    isLoading: profileLoading,
    isError: profileError,
    refetch,
  } = useFetchGeneticProfileQuery();

  const { data: knownMutations = [], isLoading: kmLoading } = useFetchKnownMutationsQuery();

  const [patchProfile, { isLoading: savingMeta }] = usePatchGeneticProfileMutation();
  const [createVariant, { isLoading: creatingCatalog }] = useCreateGeneticVariantMutation();
  const [patchVariant] = usePatchGeneticVariantMutation();
  const [deleteVariant, { isLoading: deletingId }] = useDeleteGeneticVariantMutation();

  useEffect(() => {
    if (profile) {
      setSource(profile.source ?? '');
      setNotes(profile.notes ?? '');
    }
  }, [profile]);

  const variants = profile?.variants ?? [];

  const handleSaveMeta = async () => {
    if (!profile?.uuid) return;
    try {
      await patchProfile({
        uuid: profile.uuid,
        source: source.trim(),
        notes: notes.trim(),
      }).unwrap();
      toast.success(t('genetics.profile-saved'));
    } catch {
      toast.error(t('genetics.error-generic'));
    }
  };

  const submitCatalog = async (body) => {
    await createVariant(body).unwrap();
    toast.success(t('genetics.variant-added'));
  };

  const submitCustom = async (body) => {
    await createVariant(body).unwrap();
    toast.success(t('genetics.variant-added'));
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('genetics.delete-confirm'))) return;
    try {
      await deleteVariant(id).unwrap();
      toast.success(t('genetics.variant-deleted'));
    } catch {
      toast.error(t('genetics.error-generic'));
    }
  };

  const handleSaveVariantEdit = async (body) => {
    if (!editingVariant) return;
    setSavingEdit(true);
    try {
      await patchVariant({ id: editingVariant.id, ...body }).unwrap();
      toast.success(t('genetics.variant-updated'));
    } finally {
      setSavingEdit(false);
    }
  };

  const renderStatusLabel = (v) => {
    if (v.apoe_genotype && String(v.apoe_genotype).trim()) {
      const key = v.apoe_genotype === 'unknown' ? 'apoe-unknown' : `apoe-${v.apoe_genotype}`;
      return t(`genetics.${key}`, { defaultValue: v.apoe_genotype });
    }
    if (v.genotype_status) {
      return t(`genetics.status-${v.genotype_status}`, { defaultValue: v.genotype_status });
    }
    if (v.custom_status_text) return v.custom_status_text;
    return '—';
  };

  if (profileLoading || kmLoading) {
    return (
      <div className='py-5 text-center text-muted'>
        <Spinner animation='border' className='me-2' />
        {t('genetics.loading')}
      </div>
    );
  }

  if (profileError || !profile) {
    return (
      <div className='py-4'>
        <Alert variant='danger'>{t('genetics.error-load')}</Alert>
        <Button variant='outline-primary' onClick={() => refetch()}>
          {t('genetics.retry')}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className='d-flex justify-content-between flex-wrap align-items-start py-4 gap-3'>
        <div>
          <h4 className='mb-1'>{t('genetics.title')}</h4>
          <p className='mb-0 text-muted'>{t('genetics.subtitle')}</p>
        </div>
        <Button variant='primary' onClick={() => setModalOpen(true)}>
          {t('genetics.add-variant')}
        </Button>
      </div>

      <Card className='shadow border-0 mb-4'>
        <Card.Body>
          <p className='text-muted small mb-4'>{t('genetics.profile-meta-description')}</p>
          <Row>
            <Col md={6} className='mb-3'>
              <Form.Group>
                <Form.Label>{t('genetics.profile-source')}</Form.Label>
                <Form.Control value={source} onChange={(e) => setSource(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={6} className='mb-3'>
              <Form.Group>
                <Form.Label>{t('genetics.profile-notes')}</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Button variant='outline-primary' onClick={handleSaveMeta} disabled={savingMeta}>
            {savingMeta ? (
              <Spinner animation='border' size='sm' />
            ) : (
              t('genetics.save-profile-meta')
            )}
          </Button>
        </Card.Body>
      </Card>

      <h5 className='mb-3'>{t('genetics.variants-title')}</h5>
      {variants.length === 0 ? (
        <Card className='genetics-empty-state border-0 mb-0 overflow-hidden'>
          <Card.Body className='genetics-empty-state__body'>
            <p className='genetics-empty-state__intro'>{t('genetics.empty-state-intro')}</p>
            <h6 className='genetics-empty-state__title'>{t('genetics.empty-state-title')}</h6>
            <ol className='genetics-empty-state__list'>
              <li>{t('genetics.empty-state-step1')}</li>
              <li>{t('genetics.empty-state-step2')}</li>
              <li>{t('genetics.empty-state-step3')}</li>
            </ol>
          </Card.Body>
        </Card>
      ) : (
        <Row className='g-3'>
          {variants.map((v) => {
            const title = variantTitle(v) || t('genetics.custom-fallback');
            const km = v.known_mutation;
            const subtitleParts = [];
            if (km?.description) subtitleParts.push(km.description);
            if (km?.rs_id) subtitleParts.push(`${t('genetics.rs-prefix')}: ${km.rs_id}`);
            if (v.custom_variant) subtitleParts.push(v.custom_variant);
            return (
              <Col md={6} lg={4} key={v.id}>
                <Card className='shadow-sm border-0 h-100'>
                  <Card.Body className='d-flex flex-column'>
                    <div className='d-flex justify-content-between align-items-start gap-2 mb-2'>
                      <Card.Title className='h6 mb-0'>{title}</Card.Title>
                      <Badge bg={statusBadgeVariant(v)}>{renderStatusLabel(v)}</Badge>
                    </div>
                    {subtitleParts.length > 0 ? (
                      <Card.Text className='small text-muted flex-grow-1'>
                        {subtitleParts.join(' · ')}
                      </Card.Text>
                    ) : null}
                    {v.custom_category ? (
                      <Card.Text className='small mb-2'>
                        <Badge bg='light' text='dark' className='fw-normal'>
                          {v.custom_category}
                        </Badge>
                      </Card.Text>
                    ) : null}
                    {v.notes ? (
                      <Card.Text className='small fst-italic text-muted mb-2'>{v.notes}</Card.Text>
                    ) : null}
                    <div className='d-flex flex-wrap gap-2 mt-auto pt-2'>
                      <Button
                        size='sm'
                        variant='outline-primary'
                        onClick={() => setEditingVariant(v)}
                      >
                        {t('genetics.edit')}
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-danger'
                        className='ms-auto'
                        disabled={deletingId}
                        onClick={() => handleDelete(v.id)}
                      >
                        {t('genetics.delete')}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <AddVariantModal
        show={modalOpen}
        onHide={() => setModalOpen(false)}
        knownMutations={knownMutations}
        onSubmitCatalog={submitCatalog}
        onSubmitCustom={submitCustom}
        catalogSubmitting={creatingCatalog}
        customSubmitting={creatingCatalog}
      />

      <EditVariantModal
        show={Boolean(editingVariant)}
        variant={editingVariant}
        onHide={() => setEditingVariant(null)}
        onSubmit={handleSaveVariantEdit}
        submitting={savingEdit}
      />
    </>
  );
}
